import React from 'react';
import NumericStepper from './NumericStepper';

interface ManualLashingItem {
    id: string;
    ropeKey: string;
    count: number;
    position?: 'above' | 'below';
    angle?: number;
}

interface DirectLashingListProps {
    manualDirectDirections: string[];
    manualLashings: any;
    compatibleRopes: any[];
    addLashing: (direction: any) => void;
    removeLashing: (direction: any, index: number) => void;
    handleManualLashingChange: (direction: any, field: any, value: any, index?: number) => void;
    t: (key: string) => string;
    isAdmin: boolean;
    lashingDisplayMode?: 'msl' | 'cordstrap';
    transportUnit?: string;
}

const DirectLashingList: React.FC<DirectLashingListProps> = ({
    manualDirectDirections,
    manualLashings,
    compatibleRopes,
    addLashing,
    removeLashing,
    handleManualLashingChange,
    t,
    isAdmin,
    lashingDisplayMode = 'msl',
    transportUnit,
}) => {
    return (
        <div className="space-y-3">
            {manualDirectDirections.map((dir) => {
                const directionKey = dir;
                const lashings = manualLashings[directionKey] as ManualLashingItem[];

                return (
                    <div key={dir} className="p-3 bg-white dark:bg-[#202c42] rounded-xl border border-slate-200 dark:border-slate-400/20 shadow-sm transition-all duration-300 hover:shadow-md dark:hover:border-slate-400/35 dark:backdrop-blur-md">
                        <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                                <h4 className="text-[10px] font-black tracking-widest text-slate-500 dark:text-slate-300">
                                    {t(`directions.${dir}`)}
                                </h4>
                            </div>
                            <button
                                onClick={() => addLashing(directionKey)}
                                className="p-1 bg-orange-50 dark:bg-orange-950/30 text-orange-600 dark:text-orange-400 rounded-md hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-all active:scale-90"
                                title="Add Lashing"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-3">
                            {lashings.map((lashing, index) => (
                                <div key={lashing.id} className="relative pl-3 border-l-2 border-slate-100 space-y-2">
                                    <div className="flex gap-2 items-start">
                                        <div className="flex-1">
                                            <select
                                                value={lashing.ropeKey}
                                                onChange={(e) => handleManualLashingChange(directionKey, 'ropeKey', e.target.value, index)}
                                                className="w-full px-2 py-1.5 bg-slate-50 dark:bg-[#26344d] border border-slate-200 dark:border-slate-400/40 rounded-md text-slate-800 dark:text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 text-xs font-medium transition-all backdrop-blur-sm"
                                            >
                                                <option value="">{t('select')}</option>
                                                {compatibleRopes.map(h => (
                                                    <option key={`${h.name}-${h.toka}`} value={`${h.name}-${h.toka}`}>
                                                        {lashingDisplayMode === 'cordstrap' && h.cordstrap_name
                                                            ? h.cordstrap_name
                                                            : (h.msl_tur || h.name)} {isAdmin && `(D_Lashing_MSL: ${h.d_lashing_msl} kN)`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {(directionKey === 'right' || directionKey === 'left' || transportUnit === 'breakbulk') && (
                                            <div className="w-[75px] shrink-0">
                                                <div className="flex bg-slate-100 dark:bg-white/5 p-0.5 rounded-md border border-slate-200 dark:border-white/10 shadow-inner">
                                                    <button
                                                        onClick={() => handleManualLashingChange(directionKey, 'angle', 45, index)}
                                                        className={`flex-1 py-1 text-[9px] font-bold rounded transition-all ${lashing.angle === 45 || !lashing.angle
                                                            ? 'bg-white dark:bg-orange-600 text-orange-600 dark:text-white shadow-sm dark:shadow-none'
                                                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white'
                                                            }`}
                                                    >
                                                        45°
                                                    </button>
                                                    <button
                                                        onClick={() => handleManualLashingChange(directionKey, 'angle', 90, index)}
                                                        className={`flex-1 py-1 text-[9px] font-bold rounded transition-all ${lashing.angle === 90
                                                            ? 'bg-white dark:bg-orange-600 text-orange-600 dark:text-white shadow-sm dark:shadow-none'
                                                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white'
                                                            }`}
                                                    >
                                                        90°
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {lashings.length > 1 && (
                                            <button
                                                onClick={() => removeLashing(directionKey, index)}
                                                className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-md transition-all active:scale-90"
                                                title="Remove Lashing"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2 items-center">
                                        <div className="flex-1 w-full bg-slate-50 dark:bg-white/5 px-2 py-1 rounded-lg border border-slate-100 dark:border-white/10">
                                            <NumericStepper
                                                label={t('direct_lashing_quantity')}
                                                value={lashing.count}
                                                min={0}
                                                step={2}
                                                disableWheel={true}
                                                compact
                                                labelPosition="inline"
                                                onChange={(value) => handleManualLashingChange(directionKey, 'count', value, index)}
                                            />
                                        </div>
                                        <div className="flex-1 w-full">
                                            <select
                                                value={lashing.position || 'above'}
                                                onChange={(e) => handleManualLashingChange(directionKey, 'position', e.target.value, index)}
                                                className="w-full h-8 px-2 bg-slate-50 dark:bg-[#26344d] border border-slate-200 dark:border-slate-400/40 rounded-md text-[10px] font-bold text-slate-700 dark:text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all backdrop-blur-sm"
                                            >
                                                <option value="above">{t('labels.cog_above')}</option>
                                                <option value="below">{t('labels.cog_below')}</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default DirectLashingList;
